import { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useCity } from "../context/CityContext";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import Logo from "../components/Logo";
import PhotoUpload from "../components/PhotoUpload";
import { photoUrl } from "../lib/photos";
import { HandHeart, HandshakeIcon, ArrowRight, CheckCircle2, Sparkles, Star, ShieldCheck } from "lucide-react";
import GoogleAuthButton from "../components/GoogleAuthButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_HEROES = {
  customer: "https://images.unsplash.com/photo-1544027993-37dbfe43562a?w=900&h=900&fit=crop&auto=format&q=75",
  assistant: "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=900&h=900&fit=crop&auto=format&q=75",
};

const ROLE_CARDS = [
  {
    id: "customer",
    title: "I Need Help",
    subtitle: "Book a verified real Mate",
    color: "var(--primary)",
    accent: "#00604A",
    Icon: HandHeart,
    tiers: [
      { id: "free", price: 0, label: "Standard", tag: "Pay per booking",
        perks: ["Book any of 13 Assists", "Verified human Mates", "Rate after every task"] },
      { id: "paid", price: 99, label: "Priority", lookup_key: "sub_priority_monthly", tag: "₹99/mo",
        perks: ["Instant WhatsApp connect", "Priority match <10 min", "10% off every booking"] },
    ],
  },
  {
    id: "assistant",
    title: "I Want to Help",
    subtitle: "Earn as a verified Mate",
    color: "var(--secondary)",
    accent: "#F26522",
    Icon: HandshakeIcon,
    tiers: [
      { id: "free", price: 0, label: "Standard Mate", tag: "Free listing",
        perks: ["Get matched to jobs in your city", "Keep 67% of every booking", "Rated after every task"] },
      { id: "paid", price: 199, label: "Featured Mate", lookup_key: "sub_featured_mate_monthly", tag: "₹199/mo",
        perks: ["Top of every search in your city", "Verified · Featured badge", "Priority job alerts"] },
    ],
  },
];

export default function Register() {
  const { register, token, authFetch } = useAuth();
  const { cities } = useCity();
  const nav = useNavigate();
  const loc = useLocation();

  // Read intent from query e.g. /register?role=assistant&tier=paid
  const initial = new URLSearchParams(loc.search);
  const [role, setRole] = useState(initial.get("role") || null);
  const [tier, setTier] = useState(initial.get("tier") || null);

  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", city: "pune" });
  const [photoPath, setPhotoPath] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (cities.length && !form.city) setForm((f) => ({ ...f, city: cities[0].slug }));
  }, [cities, form.city]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const selectedRole = ROLE_CARDS.find((r) => r.id === role);
  const selectedTier = selectedRole?.tiers.find((t) => t.id === tier);

  const submit = async (e) => {
    e.preventDefault();
    if (!role || !tier) {
      toast.error("Choose a role and a tier");
      return;
    }
    setBusy(true);
    try {
      const user = await register({
        name: form.name, email: form.email, password: form.password,
        phone: form.phone, city: form.city, role,
      });
      // Photo uploaded already (needed token); if not uploaded yet, skip
      // If paid tier, redirect to Stripe checkout
      if (tier === "paid" && selectedTier?.lookup_key) {
        const kind = role === "customer" ? "membership" : "featured_mate";
        const r = await authFetch({
          method: "POST",
          url: `${API}/payments/checkout`,
          data: { lookup_key: selectedTier.lookup_key, quantity: 1, origin_url: window.location.origin, kind },
        });
        window.location.href = r.data.checkout_url;
        return;
      }
      toast.success("Welcome to HeylpMate");
      nav(role === "assistant" ? "/become-mate" : "/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
      setBusy(false);
    }
  };

  // Step 1: pick role
  if (!role) {
    return (
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-14 md:py-20">
        <div className="text-center max-w-2xl mx-auto">
          <Link to="/" className="inline-block mb-6"><Logo tagline /></Link>
          <span className="label-eyebrow">get started in seconds</span>
          <h1 className="font-display text-5xl md:text-6xl font-bold mt-3 leading-tight">
            Which side of <span className="italic" style={{ color: "var(--secondary)" }}>HeylpMate</span> are you on?
          </h1>
          <p className="mt-4 text-[color:var(--text-muted)] text-lg">Pick one — you can always switch later.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6 mt-12">
          {ROLE_CARDS.map((r) => (
            <button
              key={r.id}
              data-testid={`role-card-${r.id}`}
              onClick={() => setRole(r.id)}
              className="group relative rounded-3xl overflow-hidden text-left card-lift shadow-xl"
              style={{ minHeight: 480 }}
            >
              <img
                src={ROLE_HEROES[r.id]}
                alt={r.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(180deg, ${r.accent}25 0%, ${r.accent}D9 60%, ${r.accent}F0 100%)`,
                }}
              />
              <div className="relative flex flex-col justify-end h-full p-7 md:p-9 text-white" style={{ minHeight: 480 }}>
                <div className="w-14 h-14 rounded-full grid place-items-center bg-white/95 mb-4">
                  <r.Icon className="w-7 h-7" style={{ color: r.accent }} />
                </div>
                <div className="text-xs uppercase tracking-[0.3em] font-bold text-white/80">
                  {r.id === "customer" ? "Help Takers" : "Help Givers"}
                </div>
                <h2 className="font-display text-4xl md:text-5xl font-bold mt-1 leading-tight">{r.title}</h2>
                <p className="mt-2 text-white/90 text-base md:text-lg max-w-sm">{r.subtitle}</p>
                <div className="mt-5 space-y-1.5 text-sm">
                  {r.tiers.map((t) => (
                    <div key={t.id} className="flex items-center justify-between text-white/90">
                      <span>{t.label}</span>
                      <span className="font-bold">{t.price === 0 ? "Free" : `₹${t.price}/mo`}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-5 inline-flex items-center gap-2 font-bold bg-white px-5 py-2.5 rounded-full w-fit group-hover:gap-3 transition-all"
                     style={{ color: r.accent }}>
                  Continue <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Quick Google entry — creates a customer account by default */}
        <div className="mt-8 max-w-md mx-auto">
          <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-neutral-400 mb-4">
            <span className="flex-1 h-px bg-black/10" /> or one-tap signup <span className="flex-1 h-px bg-black/10" />
          </div>
          <GoogleAuthButton role="customer" testid="google-register-quick" label="Continue with Google (as Help Taker)" />
        </div>

        <p className="mt-10 text-center text-sm text-neutral-500">
          Already have an account?{" "}
          <Link to="/login" data-testid="register-login-link" className="font-bold underline" style={{ color: "var(--secondary)" }}>Sign in</Link>
        </p>
      </div>
    );
  }

  // Step 2: pick tier
  if (!tier) {
    return (
      <div className="max-w-4xl mx-auto px-5 md:px-8 py-14 md:py-20">
        <button data-testid="tier-back" onClick={() => setRole(null)} className="text-sm text-neutral-500 hover:text-[color:var(--primary)]">← Back</button>
        <div className="text-center mt-4">
          <span className="label-eyebrow">{selectedRole.title}</span>
          <h1 className="font-display text-4xl md:text-5xl font-bold mt-3 leading-tight">
            Choose your <span className="italic" style={{ color: "var(--secondary)" }}>tier</span>.
          </h1>
          <p className="mt-3 text-[color:var(--text-muted)]">Both tiers unlock the full HeylpMate experience — Paid gives you an edge.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6 mt-12">
          {selectedRole.tiers.map((t) => {
            const paid = t.id === "paid";
            return (
              <button
                key={t.id}
                data-testid={`tier-card-${selectedRole.id}-${t.id}`}
                onClick={() => setTier(t.id)}
                className={`text-left rounded-3xl border-2 p-8 card-lift ${paid ? "text-white" : "bg-white"}`}
                style={{
                  borderColor: paid ? selectedRole.accent : "rgba(0,0,0,0.1)",
                  background: paid ? selectedRole.accent : "#fff",
                }}
              >
                <div className="flex items-center gap-2">
                  {paid && <Sparkles className="w-4 h-4" />}
                  <span className={`text-xs uppercase tracking-widest font-bold ${paid ? "text-white/80" : ""}`} style={paid ? {} : { color: selectedRole.accent }}>{t.tag}</span>
                </div>
                <div className="font-display text-4xl mt-3">{t.price === 0 ? "Free" : `₹${t.price}`}</div>
                <div className={`text-sm ${paid ? "text-white/70" : "text-neutral-500"}`}>{t.label}</div>
                <ul className="mt-6 space-y-2 text-sm">
                  {t.perks.map((p) => (
                    <li key={p} className="flex gap-2"><CheckCircle2 className={`w-4 h-4 shrink-0 mt-0.5 ${paid ? "" : ""}`} style={{ color: paid ? "#fff" : selectedRole.accent }} /> {p}</li>
                  ))}
                </ul>
                <div className={`mt-8 inline-flex items-center gap-1 text-sm font-bold ${paid ? "text-white" : ""}`} style={paid ? {} : { color: selectedRole.accent }}>
                  Select <ArrowRight className="w-4 h-4" />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Step 3: profile form
  return (
    <div className="min-h-[85vh] grid place-items-center px-5 py-10">
      <div className="w-full max-w-lg">
        <button data-testid="form-back" onClick={() => setTier(null)} className="text-sm text-neutral-500 hover:text-[color:var(--primary)]">← Back</button>
        <Link to="/" className="inline-block mb-6 mt-4"><Logo tagline /></Link>
        <div className="inline-flex items-center gap-2 mb-3">
          <span className="chip"><selectedRole.Icon className="w-3.5 h-3.5" /> {selectedRole.title}</span>
          <span className="chip" style={{ borderColor: selectedRole.accent, color: selectedRole.accent }}>
            {selectedTier.label} · {selectedTier.price === 0 ? "Free" : `₹${selectedTier.price}/mo`}
          </span>
        </div>
        <h1 className="font-display text-4xl font-bold">Create your account.</h1>
        <p className="mt-1" style={{ color: "var(--text-muted)" }}>Your photo makes a real difference in trust.</p>

        {/* One-tap Google option (only for free tier — paid tier needs Stripe redirect that ties to email/pw form) */}
        {selectedTier.price === 0 && (
          <div className="mt-5">
            <GoogleAuthButton role={role} testid={`google-register-${role}`} label={`Continue with Google as ${selectedRole.title}`} />
            <div className="my-4 flex items-center gap-3 text-[11px] uppercase tracking-widest text-neutral-400">
              <span className="flex-1 h-px bg-black/10" /> or use email <span className="flex-1 h-px bg-black/10" />
            </div>
          </div>
        )}

        <form onSubmit={submit} className="mt-6 space-y-4">
          {/* Photo upload — token-gated: only after account exists */}
          {token ? (
            <PhotoUpload value={photoPath} token={token} onUpload={setPhotoPath} />
          ) : (
            <div className="rounded-2xl border border-dashed border-black/15 bg-white p-4 text-xs text-neutral-500">
              You can add a photo right after account creation.
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-500">Full name</Label>
            <Input data-testid="register-name-input" className="mt-1.5" value={form.name} onChange={upd("name")} required />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-neutral-500">Email</Label>
              <Input data-testid="register-email-input" type="email" className="mt-1.5" value={form.email} onChange={upd("email")} required />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-neutral-500">Phone</Label>
              <Input data-testid="register-phone-input" className="mt-1.5" placeholder="+91…" value={form.phone} onChange={upd("phone")} />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-500">City</Label>
            <select
              data-testid="register-city-select"
              value={form.city}
              onChange={upd("city")}
              className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {cities.map((c) => <option key={c.slug} value={c.slug}>{c.name} {c.status === "live" ? "· Live" : "· Soon"}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-500">Password</Label>
            <Input data-testid="register-password-input" type="password" className="mt-1.5" minLength={6} value={form.password} onChange={upd("password")} required />
          </div>
          <button data-testid="register-submit-btn" className="btn-primary w-full inline-flex items-center justify-center gap-2" disabled={busy}>
            {busy ? "Creating account…" : selectedTier.price === 0 ? "Create account · Free" : `Create & Pay ₹${selectedTier.price}/mo`}
            <ArrowRight className="w-4 h-4" />
          </button>
          <div className="text-[11px] text-neutral-500 text-center">
            {selectedTier.price > 0 ? "You'll be redirected to Stripe to complete subscription." : "You can upgrade to Paid anytime from your dashboard."}
          </div>
        </form>

        <p className="mt-6 text-sm text-neutral-500">
          Already have an account?{" "}
          <Link to="/login" data-testid="register-login-link" className="font-bold underline" style={{ color: "var(--secondary)" }}>Sign in</Link>
        </p>
      </div>
    </div>
  );
}
